import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Camera, Gift, FileText, Feather } from 'lucide-react';
import { useCourse } from '@/hooks/useCourse';
import { useTelegram } from '@/hooks/useTelegram';
import { Loader } from '@/components/Loader';

// ════════════════════════════════════════════════════════════════
// Веха дня 21: конфетти → А→Б → фото до/после → карточка → награда.
// «Веха, не финиш» — фундамент заложен.
// ════════════════════════════════════════════════════════════════

export default function MilestoneScreen() {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const { progress, loading, getSignedPhotoUrl, uploadProgressPhoto, setPointBPhoto, unlockReward } = useCourse();

  const [beforeUrl, setBeforeUrl] = useState<string | null>(null);
  const [afterUrl, setAfterUrl] = useState<string | null>(null);
  const [reflection, setReflection] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (loading || firedRef.current) return;
    firedRef.current = true;
    confetti({ particleCount: 120, spread: 75, origin: { y: 0.4 }, colors: ['#10b981', '#06b6d4', '#84cc16'] });
    haptic('success');
  }, [loading, haptic]);

  useEffect(() => {
    (async () => {
      if (progress?.point_a_photo_path) setBeforeUrl(await getSignedPhotoUrl(progress.point_a_photo_path));
      if (progress?.point_b_photo_path) setAfterUrl(await getSignedPhotoUrl(progress.point_b_photo_path));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress?.point_a_photo_path, progress?.point_b_photo_path]);

  async function takeAfter(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    haptic('medium');
    try {
      const path = await uploadProgressPhoto(f, 'b');
      if (path) {
        await setPointBPhoto(path);
        setAfterUrl(await getSignedPhotoUrl(path));
      }
    } finally { setUploading(false); }
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader label="Собираем твой путь…" /></div>;
  if (!progress) { nav('/path'); return null; }

  const pointA = progress.point_a as any;

  return (
    <div className="px-5 pt-8 pb-28 max-w-md mx-auto">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
        <div className="text-5xl mb-2">🎉🍎</div>
        <h1 className="font-display text-2xl font-bold leading-tight">Ты прошёл Путь.<br />Фундамент заложен.</h1>
        <p className="text-sm text-ink-dim mt-2">Это веха, а не финиш — привычка крепнет дальше. Ты молодец 💚</p>
      </motion.div>

      {/* А → Б */}
      <div className="mt-6 grid grid-cols-2 gap-2">
        <div className="bg-bg-card border border-line rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-wide text-ink-faint mb-1">Было · день 1</div>
          <div className="text-sm">{pointA?.words || [pointA?.mood, pointA?.energy].filter(Boolean).join(' · ') || '—'}</div>
        </div>
        <div className="bg-emerald-glow/10 border border-emerald-glow/40 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-wide text-emerald-glow mb-1">Стало · сегодня</div>
          <textarea value={reflection} onChange={(e) => setReflection(e.target.value)} rows={2} maxLength={200}
            placeholder="как ты сейчас?" className="input text-sm w-full resize-none bg-transparent border-none p-0" />
        </div>
      </div>

      {/* Фото до/после */}
      <div className="mt-4">
        {beforeUrl ? (
          <div className="grid grid-cols-2 gap-2">
            <figure className="m-0">
              <img src={beforeUrl} alt="" className="w-full aspect-square object-cover rounded-xl border border-line" />
              <figcaption className="text-[10px] text-ink-faint text-center mt-1">День 1</figcaption>
            </figure>
            {afterUrl ? (
              <figure className="m-0">
                <img src={afterUrl} alt="" className="w-full aspect-square object-cover rounded-xl border border-emerald-glow/40" />
                <figcaption className="text-[10px] text-emerald-glow text-center mt-1">Сегодня</figcaption>
              </figure>
            ) : (
              <button onClick={() => fileRef.current?.click()} disabled={uploading}
                className="aspect-square rounded-xl border-2 border-dashed border-line flex flex-col items-center justify-center text-ink-dim text-xs gap-1 active:scale-[0.98]">
                <Camera size={20} />{uploading ? 'Загружаем…' : 'Фото сейчас'}
              </button>
            )}
          </div>
        ) : (
          <p className="text-[11px] text-ink-faint text-center">Фото в начале ты не делал — сравним по ощущениям 💚</p>
        )}
        <input ref={fileRef} type="file" accept="image/*" capture="user" onChange={takeAfter} className="hidden" />
      </div>

      {/* Карточка для репоста (эмоция, не вес) */}
      {reflection.trim() && (
        <div className="mt-5 rounded-2xl border border-emerald-glow/40 p-4" style={{ background: 'linear-gradient(160deg, rgba(16,185,129,.12), transparent)' }}>
          <div className="text-base font-semibold leading-snug">«{reflection.trim()}»</div>
          <div className="text-[11px] text-ink-faint mt-2">— мой Путь со Славой · slava-health</div>
        </div>
      )}

      {/* Награда-набор */}
      <div className="mt-6 space-y-2">
        {!progress.reward_unlocked ? (
          <button onClick={() => { haptic('success'); unlockReward(); }}
            className="w-full py-3.5 rounded-xl bg-warn text-[#1a1204] font-bold flex items-center justify-center gap-1.5 active:scale-[0.98]">
            <Gift size={17} /> Забрать награду за прохождение
          </button>
        ) : (
          <>
            <div className="rounded-xl bg-warn/15 border border-warn/40 p-3.5 flex items-center gap-3">
              <Gift size={18} className="text-warn shrink-0" />
              <div className="text-sm"><b>Скидка −20% на консультацию</b><br /><span className="text-ink-dim text-xs">Активна в твоём профиле</span></div>
            </div>
            <button onClick={() => haptic('light')}
              className="w-full rounded-xl bg-bg-card border border-line p-3.5 flex items-center gap-3 text-left active:scale-[0.99]">
              <FileText size={18} className="text-emerald-glow shrink-0" />
              <div className="text-sm"><b>Гид «5 опор: как удержать»</b><br /><span className="text-ink-dim text-xs">Скачать PDF</span></div>
            </button>

            {/* Стихотворение стоит ПОСЛЕ награды и отдельным блоком: это не бонус
                к скидке, а личный подарок. Рядом со скидкой оно читалось бы
                как приём продажи и потеряло бы искренность. */}
            <button onClick={() => { haptic('light'); nav('/path/poem'); }}
              className="w-full rounded-xl border border-emerald-glow/40 p-3.5 flex items-center gap-3 text-left active:scale-[0.99]"
              style={{ background: 'linear-gradient(150deg, rgba(16,185,129,.12), transparent)' }}>
              <Feather size={18} className="text-emerald-glow shrink-0" />
              <div className="text-sm"><b>Твой путь в стихах</b><br /><span className="text-ink-dim text-xs">Дофи написал о твоих трёх неделях</span></div>
            </button>
          </>
        )}
        <button onClick={() => nav('/')} className="w-full py-3 rounded-xl bg-bg-elevated text-ink-muted text-sm active:opacity-70">
          На главную
        </button>
      </div>
    </div>
  );
}
